// v3.21-1680 · Funding Health — internal-only operational reporting.
// Replaces Manager Dashboard.  Read-only.  Internal-user only (backend
// enforces 403; sidebar item hidden for external partners).
import { useEffect, useMemo, useState, useCallback } from 'react';
import { Loader2, RefreshCw, Warehouse, BarChart3, AlertTriangle, DollarSign, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../lib/api';

const fmtMoney = (n) => (n == null || isNaN(n)) ? '—' :
  Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmtPct   = (n) => (n == null ? '—' : `${n}%`);
const fmtDays  = (n) => (n == null ? '—' : `${n} d`);
const fmtInt   = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US'));

const PERIODS = [
  { value: '7d',  label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: 'mtd', label: 'MTD' },
  { value: 'qtd', label: 'QTD' },
  { value: 'ytd', label: 'YTD' },
  { value: 'all', label: 'All Time' },
];

const AREAS = [
  { value: 'overall',  label: 'Overall' },
  { value: 'consumer', label: 'Consumer Funding' },
  { value: 'business', label: 'Business Funding' },
  { value: 'warehouse',label: 'Lead Warehouse' },
];

const Card = ({ label, value, sub, testId, tone = 'default' }) => {
  const toneCls = {
    default:   'bg-white',
    warehouse: 'bg-slate-50',
    funded:    'bg-emerald-50',
    ratio:     'bg-indigo-50/60',
    timing:    'bg-amber-50/60',
    attention: 'bg-rose-50',
  }[tone] || 'bg-white';
  return (
    <div className={`border rounded p-4 ${toneCls}`} data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-slate-900" data-testid={`${testId}-value`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500" data-testid={`${testId}-sub`}>{sub}</div>}
    </div>
  );
};

export default function FundingHealthPage() {
  const [period, setPeriod] = useState('30d');
  const [area, setArea]     = useState('overall');
  const [overall, setOverall] = useState(null);
  const [needs, setNeeds]     = useState(null);
  const [warehouse, setWarehouse] = useState(null);
  const [consumer, setConsumer]   = useState(null);
  const [business, setBusiness]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [o, n, w, c, b] = await Promise.all([
        api.get(`/funding-health/overall?period=${period}`),
        api.get(`/funding-health/needs-attention?period=${period}`),
        api.get(`/funding-health/lead-warehouse?period=${period}`),
        api.get(`/funding-health/consumer?period=${period}`),
        api.get(`/funding-health/business?period=${period}`),
      ]);
      setOverall(o.data);
      setNeeds(n.data);
      setWarehouse(w.data);
      setConsumer(c.data);
      setBusiness(b.data);
      setLastRefreshed(new Date());
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load Funding Health');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const top = overall?.top || {};
  const bd  = overall?.breakdown || {};
  const wh  = warehouse || {};
  const cd  = consumer?.metrics || {};
  const cst = consumer?.stages || {};
  const bs  = business?.metrics || {};

  const showAll       = area === 'overall';
  const showConsumer  = showAll || area === 'consumer';
  const showBusiness  = showAll || area === 'business';
  const showWarehouse = showAll || area === 'warehouse';

  return (
    <div className="space-y-5 p-2" data-testid="funding-health-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl text-slate-900" data-testid="funding-health-title">Funding Health</h1>
          <p className="text-sm text-slate-500 mt-1">
            Operational health across Lead Warehouse, Consumer Funding, and Business Funding.
            Reporting only — this page never modifies records.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-28" data-testid="fh-period"><SelectValue /></SelectTrigger>
            <SelectContent>{PERIODS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={area} onValueChange={setArea}>
            <SelectTrigger className="w-48" data-testid="fh-area"><SelectValue /></SelectTrigger>
            <SelectContent>{AREAS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="fh-refresh">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Refresh
          </Button>
          {lastRefreshed && (
            <span className="text-[11px] text-slate-400" data-testid="fh-last-refreshed">
              Last Refreshed: {lastRefreshed.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      {error && <div className="border border-rose-200 bg-rose-50 rounded p-3 text-sm text-rose-800" data-testid="fh-error">{error}</div>}
      {loading && !overall && (
        <div className="p-6" data-testid="fh-loading"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      )}

      {/* SECTION 1 — Overall Funding Health */}
      {showAll && overall && (
        <section data-testid="fh-section-overall">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="h-4 w-4 text-slate-500" />
            <h2 className="font-heading text-lg text-slate-800">Overall Funding Health</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <Card testId="fh-top-submitted" label="Applications Submitted" value={fmtInt(top.applications_submitted)} />
            <Card testId="fh-top-approved"  label="Applications Approved"  value={fmtInt(top.applications_approved)} />
            <Card testId="fh-top-funded"    label="Applications Funded"    value={fmtInt(top.applications_funded)}    tone="funded" />
            <Card testId="fh-top-volume"    label="Funding Volume"         value={fmtMoney(top.funding_volume)}       tone="funded" />
            <Card testId="fh-top-approval-pct" label="Approval %"          value={fmtPct(top.approval_pct)}           sub="Approved ÷ Submitted" tone="ratio" />
            <Card testId="fh-top-app-funded-pct" label="Application → Funded %" value={fmtPct(top.application_to_funded_pct)} sub="Funded ÷ Submitted" tone="ratio" />
            <Card testId="fh-top-active"    label="Active Applications"    value={fmtInt(top.active_applications)} />
            <Card testId="fh-top-avg-app"   label="Avg Days to Application" value={fmtDays(top.avg_days_to_application)} tone="timing" />
            <Card testId="fh-top-avg-fund"  label="Avg Days to Funding"    value={fmtDays(top.avg_days_to_funding)}    tone="timing" />
          </div>
          <div className="mt-3 border rounded p-3 bg-slate-50" data-testid="fh-overall-breakdown">
            <p className="text-[11px] uppercase font-semibold text-slate-500 mb-2">Breakdown</p>
            <div className="grid grid-cols-4 gap-3 text-sm">
              <div className="font-semibold text-slate-700"></div>
              <div className="text-slate-500">Consumer</div>
              <div className="text-slate-500">Business</div>
              <div className="text-slate-500">Total</div>
              {[
                ['Submitted',       'submitted'],
                ['Approved',        'approved'],
                ['Funded',          'funded'],
                ['Funding Volume',  'funding_volume'],
              ].map(([label, key]) => (
                <div key={key} className="contents">
                  <div className="text-slate-700">{label}</div>
                  <div data-testid={`fh-bd-consumer-${key}`}>{key === 'funding_volume' ? fmtMoney(bd?.consumer?.[key]) : fmtInt(bd?.consumer?.[key])}</div>
                  <div data-testid={`fh-bd-business-${key}`}>{key === 'funding_volume' ? fmtMoney(bd?.business?.[key]) : fmtInt(bd?.business?.[key])}</div>
                  <div data-testid={`fh-bd-total-${key}`}   className="font-semibold">{key === 'funding_volume' ? fmtMoney(bd?.total?.[key]) : fmtInt(bd?.total?.[key])}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SECTION 2 — Needs Attention */}
      {showAll && needs && (
        <section data-testid="fh-section-attention">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-rose-500" />
            <h2 className="font-heading text-lg text-slate-800">Needs Attention</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {(needs.items || []).map(item => (
              <Card key={item.key} testId={`fh-attn-${item.key}`} label={item.label} value={fmtInt(item.count)} tone="attention" />
            ))}
          </div>
        </section>
      )}

      {/* SECTION 3 — Lead Warehouse Health */}
      {showWarehouse && warehouse && (
        <section data-testid="fh-section-warehouse">
          <div className="flex items-center gap-2 mb-2">
            <Warehouse className="h-4 w-4 text-slate-500" />
            <h2 className="font-heading text-lg text-slate-800">Lead Warehouse Health</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card testId="fh-wh-total"     label="Warehouse Leads"    value={fmtInt(wh.warehouse_leads)} tone="warehouse" />
            <Card testId="fh-wh-assigned"  label="Assigned"           value={fmtInt(wh.by_status?.Assigned)} tone="warehouse" />
            <Card testId="fh-wh-working"   label="Working"            value={fmtInt(wh.by_status?.Working)} tone="warehouse" />
            <Card testId="fh-wh-qualified" label="Qualified"          value={fmtInt(wh.by_status?.Qualified)} tone="warehouse" />
            <Card testId="fh-wh-app-started" label="Applications Started" value={fmtInt(wh.applications_started)} />
            <Card testId="fh-wh-to-app-pct" label="Warehouse → Application %" value={fmtPct(wh.warehouse_to_application_pct)} sub="Apps ÷ (WH + Apps)" tone="ratio" />
            <Card testId="fh-wh-avg-first" label="Avg Days to First Contact" value={fmtDays(wh.avg_days_to_first_contact)} tone="timing" />
            <Card testId="fh-wh-avg-app"   label="Avg Days to Application"   value={fmtDays(wh.avg_days_to_application)}   tone="timing" />
          </div>
        </section>
      )}

      {/* SECTION 4 — Consumer Funding Health */}
      {showConsumer && consumer && (
        <section data-testid="fh-section-consumer">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="h-4 w-4 text-slate-500" />
            <h2 className="font-heading text-lg text-slate-800">Consumer Funding Health</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card testId="fh-c-submitted" label="Submitted" value={fmtInt(cd.submitted)} />
            <Card testId="fh-c-approved"  label="Approved"  value={fmtInt(cd.approved)} />
            <Card testId="fh-c-funded"    label="Funded"    value={fmtInt(cd.funded)}    tone="funded" />
            <Card testId="fh-c-volume"    label="Funding Volume" value={fmtMoney(cd.funding_volume)} tone="funded" />
            <Card testId="fh-c-approval-pct" label="Approval %" value={fmtPct(cd.approval_pct)} tone="ratio" />
            <Card testId="fh-c-app-fund-pct" label="Application → Funded %" value={fmtPct(cd.application_to_funded_pct)} tone="ratio" />
            <Card testId="fh-c-active"    label="Active" value={fmtInt(cd.active)} />
            <Card testId="fh-c-avg-fund"  label="Avg Days to Funding" value={fmtDays(cd.avg_days_to_funding)} tone="timing" />
          </div>
          {Object.keys(cst).length > 0 && (
            <div className="mt-3 border rounded p-3 bg-slate-50" data-testid="fh-c-stages">
              <p className="text-[11px] uppercase font-semibold text-slate-500 mb-2">Current States</p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
                {Object.entries(cst).map(([label, count]) => (
                  <div key={label} data-testid={`fh-c-stage-${label.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
                    <div className="text-[11px] text-slate-500 uppercase tracking-wide">{label}</div>
                    <div className="text-lg font-semibold text-slate-800">{fmtInt(count)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* SECTION 5 — Business Funding Health */}
      {showBusiness && business && (
        <section data-testid="fh-section-business">
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="h-4 w-4 text-slate-500" />
            <h2 className="font-heading text-lg text-slate-800">Business Funding Health</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card testId="fh-b-submitted" label="Submitted" value={fmtInt(bs.submitted)} />
            <Card testId="fh-b-approved"  label="Approved"  value={fmtInt(bs.approved)} />
            <Card testId="fh-b-funded"    label="Funded"    value={fmtInt(bs.funded)}    tone="funded" />
            <Card testId="fh-b-volume"    label="Funding Volume" value={fmtMoney(bs.funding_volume)} tone="funded" />
            <Card testId="fh-b-approval-pct" label="Approval %" value={fmtPct(bs.approval_pct)} tone="ratio" />
            <Card testId="fh-b-app-fund-pct" label="Application → Funded %" value={fmtPct(bs.application_to_funded_pct)} tone="ratio" />
            <Card testId="fh-b-active"    label="Active" value={fmtInt(bs.active)} />
            <Card testId="fh-b-avg-fund"  label="Avg Days to Funding" value={fmtDays(bs.avg_days_to_funding)} tone="timing" />
          </div>
        </section>
      )}

      <p className="text-[11px] text-slate-400 italic" data-testid="fh-footer">
        Sources: Lead Warehouse (clients where company = LEAD WAREHOUSE),
        Consumer Funding (clients deal_stage + loan_amount),
        Business Funding (bf_applications status + funded_amount || amount_requested).
        Funding Volume uses actual funded dollars only.  No records are modified from this page.
      </p>
    </div>
  );
}
